// Brouillon de formulaire persisté dans le navigateur (localStorage).
// Objectif : qu'un rédacteur qui perd sa connexion / recharge la page sans avoir enregistré
// retrouve sa saisie. Tout est encapsulé en try/catch (mode privé, quota, storage bloqué).
//
// Sérialisation :
//  - les dates dayjs → { __d: ISO }  (ré-hydratées en dayjs au chargement) ;
//  - les fichiers (File/Blob) et les valeurs d'Upload AntD ({ fileList } / originFileObj) sont
//    ÉCARTÉS (non sérialisables de façon fiable) : les pièces jointes ne sont pas conservées ;
//  - les fonctions sont écartées.

import dayjs from 'dayjs';

const estFichier = (v) =>
    (typeof File !== 'undefined' && v instanceof File) ||
    (typeof Blob !== 'undefined' && v instanceof Blob);

// Remplace dayjs par un marqueur, écarte fichiers/uploads/fonctions, récursivement.
const encoder = (v) => {
    if (v === null || v === undefined) return v;
    if (dayjs.isDayjs(v)) return { __d: v.toISOString() };
    if (typeof v === 'function' || estFichier(v)) return undefined;
    if (Array.isArray(v)) return v.map(encoder).filter((x) => x !== undefined);
    if (typeof v === 'object') {
        // Valeur d'un <Upload> AntD → on écarte toute la pièce jointe.
        if (Array.isArray(v.fileList) || v.originFileObj) return undefined;
        const out = {};
        for (const k of Object.keys(v)) {
            const ev = encoder(v[k]);
            if (ev !== undefined) out[k] = ev;
        }
        return out;
    }
    return v;
};

// Reconstruit les dayjs à partir des marqueurs.
const decoder = (v) => {
    if (v === null || v === undefined) return v;
    if (Array.isArray(v)) return v.map(decoder);
    if (typeof v === 'object') {
        if (typeof v.__d === 'string') return dayjs(v.__d);
        const out = {};
        for (const k of Object.keys(v)) out[k] = decoder(v[k]);
        return out;
    }
    return v;
};

/** Sauvegarde un instantané (objet quelconque) sous la clé donnée. Silencieux en cas d'échec. */
export const saveDraft = (key, snapshot) => {
    try {
        const payload = encoder({ ...snapshot, savedAt: snapshot?.savedAt || Date.now() });
        localStorage.setItem(key, JSON.stringify(payload));
        return true;
    } catch {
        return false;
    }
};

/** Charge un brouillon (dates ré-hydratées). Renvoie null si absent, illisible ou expiré. */
export const loadDraft = (key, maxAgeMs) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (maxAgeMs && parsed?.savedAt && Date.now() - parsed.savedAt > maxAgeMs) {
            localStorage.removeItem(key);
            return null;
        }
        return decoder(parsed);
    } catch {
        return null;
    }
};

/** Supprime le brouillon. */
export const clearDraft = (key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
};

/** « il y a 3 min », « il y a 2 h », « à l'instant »… à partir d'un timestamp (ms). */
export const tempsEcoule = (ts) => {
    if (!ts) return '';
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 45) return "à l'instant";
    const m = Math.floor(s / 60);
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    const j = Math.floor(h / 24);
    return `il y a ${j} j`;
};
